<script>
import NextButton from 'dashboard/components-next/button/Button.vue';
import { getWhatsappTemplateErrorMessage } from 'dashboard/helper/templateHelper';

const FOOTER_MAX_LENGTH = 60;
const HEADER_TEXT_MAX_LENGTH = 60;
const BODY_MAX_LENGTH = 1024;

const emptyButton = () => ({
  type: 'QUICK_REPLY',
  text: '',
  url: '',
  phone_number: '',
});

const initialForm = () => ({
  id: null,
  name: '',
  language: 'pt_BR',
  category: 'UTILITY',
  parameter_format: 'POSITIONAL',
  header_format: 'NONE',
  header_text: '',
  header_handle: '',
  body: '',
  footer: '',
  buttons: [],
});

const LANGUAGE_OPTIONS = [
  { value: 'pt_BR', label: 'pt-BR' },
  { value: 'en_US', label: 'en_US' },
];

export default {
  components: { NextButton },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      form: initialForm(),
      selectedTemplateId: '',
      activeView: 'templates',
      showTemplateModal: false,
      showDeleteConfirmModal: false,
      showFeedbackModal: false,
      feedbackTitle: '',
      feedbackMessage: '',
      feedbackTone: 'info',
      isSaving: false,
      isSyncing: false,
      deletingTemplateId: '',
      templatePendingDelete: null,
      languageOptions: LANGUAGE_OPTIONS,
    };
  },
  computed: {
    templates() {
      const templates =
        this.inbox.message_templates ||
        this.inbox.additional_attributes?.message_templates ||
        [];

      return Array.isArray(templates) ? templates : [];
    },
    sortedTemplates() {
      return [...this.templates].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
      );
    },
    selectedTemplate() {
      return this.templates.find(
        template => String(template.id) === String(this.selectedTemplateId)
      );
    },
    isEditing() {
      return !!this.form.id;
    },
    canSave() {
      return (
        this.form.name.trim() &&
        this.form.body.trim() &&
        this.isHeaderValid &&
        this.isBodyValid &&
        this.isFooterValid
      );
    },
    headerLength() {
      return this.form.header_text.length;
    },
    isHeaderValid() {
      return (
        this.form.header_format !== 'TEXT' ||
        this.headerLength <= HEADER_TEXT_MAX_LENGTH
      );
    },
    headerMaxLength() {
      return HEADER_TEXT_MAX_LENGTH;
    },
    bodyLength() {
      return this.form.body.length;
    },
    isBodyValid() {
      return this.bodyLength <= BODY_MAX_LENGTH;
    },
    bodyMaxLength() {
      return BODY_MAX_LENGTH;
    },
    footerLength() {
      return this.form.footer.length;
    },
    isFooterValid() {
      return this.footerLength <= FOOTER_MAX_LENGTH;
    },
    footerMaxLength() {
      return FOOTER_MAX_LENGTH;
    },
    statusCounts() {
      return this.templates.reduce((acc, template) => {
        const status = (template.status || 'PENDING').toUpperCase();
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});
    },
    approvalRate() {
      if (!this.templates.length) return 0;
      return Math.round(
        ((this.statusCounts.APPROVED || 0) / this.templates.length) * 100
      );
    },
    statusInsights() {
      return [
        {
          key: 'APPROVED',
          count: this.statusCounts.APPROVED || 0,
          barClass: 'bg-n-teal-9',
          dotClass: 'bg-n-teal-9',
        },
        {
          key: 'PENDING',
          count: this.statusCounts.PENDING || 0,
          barClass: 'bg-n-amber-9',
          dotClass: 'bg-n-amber-9',
        },
        {
          key: 'REJECTED',
          count: this.statusCounts.REJECTED || 0,
          barClass: 'bg-n-ruby-9',
          dotClass: 'bg-n-ruby-9',
        },
        {
          key: 'PAUSED',
          count: this.statusCounts.PAUSED || 0,
          barClass: 'bg-n-slate-8',
          dotClass: 'bg-n-slate-8',
        },
      ];
    },
    categoryCounts() {
      return this.templates.reduce((acc, template) => {
        const category = template.category || 'UNKNOWN';
        acc[category] = (acc[category] || 0) + 1;
        return acc;
      }, {});
    },
    languageCounts() {
      return this.templates.reduce((acc, template) => {
        const language = template.language || 'UNKNOWN';
        acc[language] = (acc[language] || 0) + 1;
        return acc;
      }, {});
    },
    lastSyncLabel() {
      const lastSync =
        this.inbox.message_templates_last_updated ||
        this.inbox.additional_attributes?.message_templates_last_updated;

      if (!lastSync)
        return this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.NEVER');
      return new Date(lastSync).toLocaleString();
    },
    previewHeader() {
      if (this.form.header_format !== 'TEXT') return '';
      return this.replaceVariables(this.form.header_text);
    },
    previewBody() {
      return this.replaceVariables(this.form.body);
    },
    bodyPlaceholder() {
      return this.form.parameter_format === 'NAMED'
        ? this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.PLACEHOLDERS.BODY_NAMED')
        : this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.PLACEHOLDERS.BODY');
    },
    variableHint() {
      return this.form.parameter_format === 'NAMED'
        ? this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.VARIABLE_HINT_NAMED')
        : this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.VARIABLE_HINT_POSITIONAL');
    },
    previewFooter() {
      return this.replaceVariables(this.form.footer);
    },
    visibleButtons() {
      return this.form.buttons.filter(button => button.text.trim());
    },
  },
  mounted() {
    this.refreshInbox();
  },
  methods: {
    getComponent(template, type) {
      return template?.components?.find(component => component.type === type);
    },
    getButtons(template) {
      return this.getComponent(template, 'BUTTONS')?.buttons || [];
    },
    getBodyPreview(template) {
      return this.getComponent(template, 'BODY')?.text || '';
    },
    templateMeta(template) {
      return [template.category, template.language].filter(Boolean).join(' / ');
    },
    statusClass(status) {
      const normalizedStatus = status?.toLowerCase();
      if (normalizedStatus === 'approved') return 'text-n-teal-11 bg-n-teal-3';
      if (normalizedStatus === 'rejected') return 'text-n-ruby-11 bg-n-ruby-3';
      return 'text-n-amber-11 bg-n-amber-3';
    },
    statusLabel(status) {
      const labels = {
        APPROVED: this.$t(
          'INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.STATUS.APPROVED'
        ),
        PENDING: this.$t(
          'INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.STATUS.PENDING'
        ),
        REJECTED: this.$t(
          'INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.STATUS.REJECTED'
        ),
        PAUSED: this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.STATUS.PAUSED'),
      };
      return labels[status] || status;
    },
    statusPercentage(count) {
      if (!this.templates.length) return 0;
      return Math.round((count / this.templates.length) * 100);
    },
    replaceVariables(text) {
      return text
        .toString()
        .replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_match, variable) => {
          const normalizedVariable = variable.trim();
          if (/^\d+$/.test(normalizedVariable)) {
            return `exemplo ${normalizedVariable}`;
          }

          if (normalizedVariable.toLowerCase() === 'nome') {
            return 'Maria Oliveira';
          }

          return `exemplo ${normalizedVariable}`;
        });
    },
    openCreateModal() {
      this.form = initialForm();
      this.selectedTemplateId = '';
      this.showTemplateModal = true;
    },
    openEditModal(template) {
      this.selectTemplate(template);
      this.showTemplateModal = true;
    },
    closeTemplateModal() {
      this.showTemplateModal = false;
    },
    addButton() {
      if (this.form.buttons.length >= 3) return;
      this.form.buttons = [...this.form.buttons, emptyButton()];
    },
    removeButton(index) {
      this.form.buttons = this.form.buttons.filter((_, i) => i !== index);
    },
    selectTemplate(template) {
      this.selectedTemplateId = template.id;
      const header = this.getComponent(template, 'HEADER');
      const body = this.getComponent(template, 'BODY');
      const footer = this.getComponent(template, 'FOOTER');

      this.form = {
        id: template.id,
        name: template.name || '',
        language: template.language || 'pt_BR',
        category: template.category || 'UTILITY',
        parameter_format: template.parameter_format || 'POSITIONAL',
        header_format: header?.format || 'NONE',
        header_text: header?.text || '',
        header_handle: '',
        body: body?.text || '',
        footer: footer?.text || '',
        buttons: this.getButtons(template).map(button => ({
          type: button.type,
          text: button.text || '',
          url: button.url || '',
          phone_number: button.phone_number || '',
        })),
      };
    },
    buildPayload() {
      return {
        name: this.form.name,
        language: this.form.language,
        category: this.form.category,
        parameter_format: this.form.parameter_format,
        header_format: this.form.header_format,
        header_text: this.form.header_text,
        header_handle: this.form.header_handle,
        body: this.form.body,
        footer: this.form.footer,
        buttons: this.form.buttons
          .filter(button => button.text.trim())
          .map(button => ({
            type: button.type,
            text: button.text,
            url: button.url,
            phone_number: button.phone_number,
          })),
      };
    },
    openFeedbackModal(title, message, tone = 'info') {
      this.feedbackTitle = title;
      this.feedbackMessage = message;
      this.feedbackTone = tone;
      this.showFeedbackModal = true;
    },
    closeFeedbackModal() {
      this.showFeedbackModal = false;
    },
    async saveTemplate() {
      if (!this.isHeaderValid) {
        this.openFeedbackModal(
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.ERROR_TITLE'),
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER_LIMIT_ERROR', {
            max: HEADER_TEXT_MAX_LENGTH,
          }),
          'error'
        );
        return;
      }
      if (!this.isBodyValid) {
        this.openFeedbackModal(
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.ERROR_TITLE'),
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.BODY_LIMIT_ERROR', {
            max: BODY_MAX_LENGTH,
          }),
          'error'
        );
        return;
      }
      if (!this.isFooterValid) {
        this.openFeedbackModal(
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.ERROR_TITLE'),
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FOOTER_LIMIT_ERROR', {
            max: FOOTER_MAX_LENGTH,
          }),
          'error'
        );
        return;
      }
      if (!this.canSave) return;
      this.isSaving = true;
      try {
        const action = this.isEditing
          ? 'inboxes/updateWhatsappTemplate'
          : 'inboxes/createWhatsappTemplate';
        const payload = this.isEditing
          ? {
              inboxId: this.inbox.id,
              templateId: this.form.id,
              template: this.buildPayload(),
            }
          : { inboxId: this.inbox.id, template: this.buildPayload() };

        await this.$store.dispatch(action, payload);
        this.openFeedbackModal(
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.SUCCESS_TITLE'),
          this.isEditing
            ? this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.UPDATE_SUCCESS')
            : this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE_SUCCESS'),
          'success'
        );
        this.closeTemplateModal();
        await this.syncTemplates();
      } catch (error) {
        this.openFeedbackModal(
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.ERROR_TITLE'),
          getWhatsappTemplateErrorMessage(
            error,
            this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.SAVE_ERROR')
          ),
          'error'
        );
      } finally {
        this.isSaving = false;
      }
    },
    openDeleteConfirmModal(template) {
      this.templatePendingDelete = template;
      this.showDeleteConfirmModal = true;
    },
    closeDeleteConfirmModal() {
      this.showDeleteConfirmModal = false;
      this.templatePendingDelete = null;
    },
    async deleteTemplate() {
      const template = this.templatePendingDelete;
      if (!template) return;

      const templateId = template.id || template.name;
      if (!templateId) return;

      this.deletingTemplateId = String(templateId);
      try {
        await this.$store.dispatch('inboxes/deleteWhatsappTemplate', {
          inboxId: this.inbox.id,
          templateId,
        });
        this.closeDeleteConfirmModal();
        this.openFeedbackModal(
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.SUCCESS_TITLE'),
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_SUCCESS'),
          'success'
        );
        await this.refreshInbox();
      } catch (error) {
        this.openFeedbackModal(
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.ERROR_TITLE'),
          error?.response?.data?.error ||
            this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_ERROR'),
          'error'
        );
      } finally {
        this.deletingTemplateId = '';
      }
    },
    async syncTemplates() {
      this.isSyncing = true;
      try {
        await this.$store.dispatch('inboxes/syncTemplates', this.inbox.id);
        setTimeout(() => this.refreshInbox(), 1200);
        await this.refreshInbox();
      } catch (error) {
        this.openFeedbackModal(
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.ERROR_TITLE'),
          this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'),
          'error'
        );
      } finally {
        this.isSyncing = false;
      }
    },
    async refreshInbox() {
      await this.$store.dispatch('inboxes/getFromNetwork');
    },
  },
};
</script>

<template>
  <div class="mx-6">
    <div class="flex items-start justify-between gap-4 mb-4">
      <div>
        <h2 class="text-heading-1 text-n-slate-12 mb-1">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11 max-w-3xl">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex gap-2">
        <NextButton
          ghost
          sm
          icon="i-lucide-refresh-cw"
          :disabled="isSyncing"
          :is-loading="isSyncing"
          :label="
            isSyncing
              ? $t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNCING')
              : $t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNC')
          "
          @click="syncTemplates"
        />
        <NextButton
          sm
          icon="i-lucide-plus"
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE')"
          @click="openCreateModal"
        />
      </div>
    </div>

    <div
      class="flex items-start gap-3 max-w-6xl p-4 mb-5 rounded-xl bg-n-blue-2 outline outline-1 outline-n-blue-5"
    >
      <span
        class="flex items-center justify-center size-8 rounded-lg bg-n-blue-4 text-n-blue-11 shrink-0"
      >
        <i class="i-lucide-clock-3 size-4" />
      </span>
      <div class="min-w-0">
        <p class="mb-1 text-sm font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.REVIEW_NOTICE_TITLE') }}
        </p>
        <p class="m-0 text-sm leading-5 text-n-slate-11">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.REVIEW_NOTICE_DESCRIPTION') }}
        </p>
      </div>
    </div>

    <div
      class="inline-flex items-center gap-1 p-1 mb-5 rounded-lg bg-n-alpha-2 outline outline-1 outline-n-weak"
    >
      <button
        type="button"
        class="px-3 py-1.5 text-sm rounded-md transition-colors"
        :class="
          activeView === 'templates'
            ? 'bg-n-background text-n-slate-12 shadow-sm'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="activeView = 'templates'"
      >
        {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABS.TEMPLATES') }}
        <span class="ml-1 text-xs text-n-slate-10">{{ templates.length }}</span>
      </button>
      <button
        type="button"
        class="px-3 py-1.5 text-sm rounded-md transition-colors"
        :class="
          activeView === 'insights'
            ? 'bg-n-background text-n-slate-12 shadow-sm'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="activeView = 'insights'"
      >
        {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABS.INSIGHTS') }}
      </button>
    </div>

    <section v-if="activeView === 'templates'" class="max-w-6xl">
      <div
        class="overflow-hidden border rounded-lg bg-n-surface-1 border-n-weak"
      >
        <div
          v-if="!sortedTemplates.length"
          class="p-8 text-center text-sm text-n-slate-11"
        >
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.EMPTY') }}
        </div>
        <div
          v-for="template in sortedTemplates"
          :key="template.id || `${template.name}-${template.language}`"
          class="grid grid-cols-1 gap-4 p-4 transition-colors border-b md:grid-cols-[minmax(0,1fr)_auto] md:items-center border-n-weak last:border-b-0 hover:bg-n-alpha-1"
        >
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2 mb-2">
              <span class="text-sm font-semibold text-n-slate-12 truncate">
                {{ template.name }}
              </span>
              <span
                class="text-xs px-2 py-0.5 rounded-md font-medium"
                :class="statusClass(template.status)"
              >
                {{ template.status || 'PENDING' }}
              </span>
            </div>
            <p class="text-sm leading-6 text-n-slate-11 line-clamp-2">
              {{ getBodyPreview(template) }}
            </p>
          </div>
          <div
            class="flex flex-wrap items-center justify-between gap-3 md:justify-end"
          >
            <div
              class="flex flex-wrap items-center gap-2 text-xs text-n-slate-10"
            >
              <span class="px-2 py-1 rounded-md bg-n-alpha-2">
                {{ template.category || 'UNKNOWN' }}
              </span>
              <span class="px-2 py-1 rounded-md bg-n-alpha-2">
                {{ template.language || 'UNKNOWN' }}
              </span>
            </div>
            <div class="flex items-center gap-1">
              <NextButton
                ghost
                xs
                icon="i-lucide-pencil"
                :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.EDIT')"
                @click="openEditModal(template)"
              />
              <NextButton
                ghost
                xs
                ruby
                icon="i-lucide-trash-2"
                :disabled="
                  deletingTemplateId === String(template.id || template.name)
                "
                :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE')"
                @click="openDeleteConfirmModal(template)"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-else class="max-w-6xl space-y-4">
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div
          class="p-4 bg-n-surface-1 outline outline-1 outline-n-weak rounded-xl"
        >
          <div class="text-xs text-n-slate-10">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.TOTAL') }}
          </div>
          <div class="text-2xl font-semibold text-n-slate-12">
            {{ templates.length }}
          </div>
        </div>
        <div
          class="p-4 bg-n-surface-1 outline outline-1 outline-n-weak rounded-xl"
        >
          <div class="text-xs text-n-slate-10">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.APPROVED') }}
          </div>
          <div class="text-2xl font-semibold text-n-teal-11">
            {{ statusCounts.APPROVED || 0 }}
          </div>
        </div>
        <div
          class="p-4 bg-n-surface-1 outline outline-1 outline-n-weak rounded-xl"
        >
          <div class="text-xs text-n-slate-10">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.PENDING') }}
          </div>
          <div class="text-2xl font-semibold text-n-amber-11">
            {{ statusCounts.PENDING || 0 }}
          </div>
        </div>
        <div
          class="p-4 bg-n-surface-1 outline outline-1 outline-n-weak rounded-xl"
        >
          <div class="text-xs text-n-slate-10">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.REJECTED') }}
          </div>
          <div class="text-2xl font-semibold text-n-ruby-11">
            {{ statusCounts.REJECTED || 0 }}
          </div>
        </div>
        <div
          class="p-4 bg-n-surface-1 outline outline-1 outline-n-weak rounded-xl"
        >
          <div class="text-xs text-n-slate-10">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.APPROVAL_RATE') }}
          </div>
          <div class="text-2xl font-semibold text-n-slate-12">
            {{
              $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.PERCENTAGE', {
                value: approvalRate,
              })
            }}
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 lg:grid-cols-[1.1fr_0.9fr]">
        <div
          class="p-5 bg-n-surface-1 outline outline-1 outline-n-weak rounded-xl"
        >
          <div class="flex items-center justify-between gap-4 mb-4">
            <h3 class="m-0 text-sm font-semibold text-n-slate-12">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.BY_STATUS') }}
            </h3>
            <span class="text-xs text-n-slate-10">
              {{
                $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.UPDATED_AT_VALUE', {
                  value: lastSyncLabel,
                })
              }}
            </span>
          </div>
          <div class="space-y-4">
            <div v-for="item in statusInsights" :key="item.key">
              <div
                class="flex items-center justify-between gap-3 mb-1.5 text-sm"
              >
                <span class="flex items-center gap-2 text-n-slate-11">
                  <span class="size-2 rounded-full" :class="item.dotClass" />
                  {{ statusLabel(item.key) }}
                </span>
                <span class="font-medium text-n-slate-12">
                  {{
                    $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.STATUS_VALUE', {
                      count: item.count,
                      percentage: statusPercentage(item.count),
                    })
                  }}
                </span>
              </div>
              <div class="h-2 overflow-hidden rounded-full bg-n-alpha-3">
                <div
                  class="h-full rounded-full transition-[width] duration-300"
                  :class="item.barClass"
                  :style="{ width: `${statusPercentage(item.count)}%` }"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">
          <div
            class="p-5 bg-n-surface-1 outline outline-1 outline-n-weak rounded-xl"
          >
            <h3 class="text-sm font-semibold text-n-slate-12 mb-3">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.BY_CATEGORY') }}
            </h3>
            <div
              v-for="(count, category) in categoryCounts"
              :key="category"
              class="flex justify-between py-2 text-sm border-b border-n-weak last:border-b-0"
            >
              <span class="text-n-slate-11">{{ category }}</span>
              <span class="font-medium text-n-slate-12">{{ count }}</span>
            </div>
          </div>
          <div
            class="p-5 bg-n-surface-1 outline outline-1 outline-n-weak rounded-xl"
          >
            <h3 class="text-sm font-semibold text-n-slate-12 mb-3">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.BY_LANGUAGE') }}
            </h3>
            <div
              v-for="(count, language) in languageCounts"
              :key="language"
              class="flex justify-between py-2 text-sm border-b border-n-weak last:border-b-0"
            >
              <span class="text-n-slate-11">{{ language }}</span>
              <span class="font-medium text-n-slate-12">{{ count }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <woot-modal
      v-model:show="showTemplateModal"
      size="whatsapp-template-modal-shell"
      :on-close="closeTemplateModal"
    >
      <div class="whatsapp-template-modal-header">
        <span class="whatsapp-template-modal-icon">
          <i class="i-lucide-message-square-text size-5" />
        </span>
        <div>
          <h2 class="mb-1 text-base font-semibold text-n-slate-12">
            {{
              isEditing
                ? $t('INBOX_MGMT.WHATSAPP_TEMPLATES.EDIT_TITLE')
                : $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE_TITLE')
            }}
          </h2>
          <p class="m-0 text-xs text-n-slate-10">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.MODAL_DESCRIPTION') }}
          </p>
        </div>
      </div>

      <div class="whatsapp-template-modal-body">
        <div class="whatsapp-template-form">
          <section class="whatsapp-template-section">
            <div class="whatsapp-template-section-heading">
              <span class="whatsapp-template-section-icon">
                <i class="i-lucide-settings-2 size-4" />
              </span>
              <div>
                <h3>
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.SECTIONS.BASIC') }}
                </h3>
                <p>
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.SECTIONS.BASIC_HINT') }}
                </p>
              </div>
            </div>
            <label class="whatsapp-template-field">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.NAME') }}
              </span>
              <input
                v-model="form.name"
                :disabled="isEditing"
                type="text"
                class="mt-1 !mb-0"
                :placeholder="
                  $t('INBOX_MGMT.WHATSAPP_TEMPLATES.PLACEHOLDERS.NAME')
                "
              />
            </label>

            <div class="whatsapp-template-two-columns">
              <label class="whatsapp-template-field">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.CATEGORY') }}
                </span>
                <select v-model="form.category" class="mt-1 !mb-0">
                  <option value="UTILITY">
                    {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORIES.UTILITY') }}
                  </option>
                  <option value="MARKETING">
                    {{
                      $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORIES.MARKETING')
                    }}
                  </option>
                  <option value="AUTHENTICATION">
                    {{
                      $t(
                        'INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORIES.AUTHENTICATION'
                      )
                    }}
                  </option>
                </select>
              </label>

              <label class="whatsapp-template-field">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.LANGUAGE') }}
                </span>
                <select
                  v-model="form.language"
                  :disabled="isEditing"
                  class="mt-1 !mb-0"
                >
                  <option
                    v-for="language in languageOptions"
                    :key="language.value"
                    :value="language.value"
                  >
                    {{ language.label }}
                  </option>
                </select>
              </label>
            </div>
          </section>

          <section class="whatsapp-template-section">
            <div class="whatsapp-template-section-heading">
              <span class="whatsapp-template-section-icon">
                <i class="i-lucide-text-cursor-input size-4" />
              </span>
              <div>
                <h3>
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.SECTIONS.CONTENT') }}
                </h3>
                <p>
                  {{
                    $t('INBOX_MGMT.WHATSAPP_TEMPLATES.SECTIONS.CONTENT_HINT')
                  }}
                </p>
              </div>
            </div>
            <label class="whatsapp-template-field">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.VARIABLE_TYPE') }}
              </span>
              <select
                v-model="form.parameter_format"
                :disabled="isEditing"
                class="mt-1 !mb-0 whatsapp-template-variable-type"
              >
                <option value="POSITIONAL">
                  {{
                    $t(
                      'INBOX_MGMT.WHATSAPP_TEMPLATES.VARIABLE_TYPES.POSITIONAL'
                    )
                  }}
                </option>
                <option value="NAMED">
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.VARIABLE_TYPES.NAMED') }}
                </option>
              </select>
            </label>

            <div
              class="whatsapp-template-two-columns whatsapp-template-header-grid"
            >
              <label class="whatsapp-template-field">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.HEADER') }}
                </span>
                <select v-model="form.header_format" class="mt-1 !mb-0">
                  <option value="NONE">
                    {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER_TYPES.NONE') }}
                  </option>
                  <option value="TEXT">
                    {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER_TYPES.TEXT') }}
                  </option>
                  <option value="IMAGE">
                    {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER_TYPES.IMAGE') }}
                  </option>
                  <option value="VIDEO">
                    {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER_TYPES.VIDEO') }}
                  </option>
                  <option value="DOCUMENT">
                    {{
                      $t('INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER_TYPES.DOCUMENT')
                    }}
                  </option>
                </select>
              </label>

              <label
                v-if="form.header_format === 'TEXT'"
                class="whatsapp-template-field"
              >
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.HEADER_TEXT') }}
                </span>
                <input
                  v-model="form.header_text"
                  type="text"
                  class="mt-1 !mb-0"
                  :class="{ '!border-n-ruby-8': !isHeaderValid }"
                  :maxlength="headerMaxLength"
                  :aria-invalid="!isHeaderValid"
                />
                <span
                  class="flex items-center justify-between gap-2 text-xs"
                  :class="isHeaderValid ? 'text-n-slate-10' : 'text-n-ruby-11'"
                >
                  <span>
                    {{
                      isHeaderValid
                        ? $t(
                            'INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER_LIMIT_HINT',
                            { max: headerMaxLength }
                          )
                        : $t(
                            'INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER_LIMIT_ERROR',
                            { max: headerMaxLength }
                          )
                    }}
                  </span>
                  <span class="shrink-0 tabular-nums">
                    {{
                      $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CHARACTER_COUNT', {
                        current: headerLength,
                        max: headerMaxLength,
                      })
                    }}
                  </span>
                </span>
              </label>
              <label
                v-if="
                  ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.header_format)
                "
                class="whatsapp-template-field"
              >
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.HEADER_HANDLE') }}
                </span>
                <input
                  v-model="form.header_handle"
                  type="text"
                  class="mt-1 !mb-0"
                  :placeholder="
                    $t(
                      'INBOX_MGMT.WHATSAPP_TEMPLATES.PLACEHOLDERS.HEADER_HANDLE'
                    )
                  "
                />
              </label>
            </div>

            <label class="whatsapp-template-field">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.BODY') }}
              </span>
              <textarea
                v-model="form.body"
                rows="5"
                class="mt-1 !mb-0"
                :class="{ '!border-n-ruby-8': !isBodyValid }"
                :maxlength="bodyMaxLength"
                :aria-invalid="!isBodyValid"
                :placeholder="bodyPlaceholder"
              />
              <span
                class="flex items-start justify-between gap-3 text-xs mt-1"
                :class="isBodyValid ? 'text-n-slate-10' : 'text-n-ruby-11'"
              >
                <span>
                  {{ variableHint }}
                  {{
                    $t('INBOX_MGMT.WHATSAPP_TEMPLATES.BODY_LIMIT_HINT', {
                      max: bodyMaxLength,
                    })
                  }}
                </span>
                <span class="shrink-0 tabular-nums">
                  {{
                    $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CHARACTER_COUNT', {
                      current: bodyLength,
                      max: bodyMaxLength,
                    })
                  }}
                </span>
              </span>
            </label>

            <label class="whatsapp-template-field">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.FOOTER') }}
              </span>
              <input
                v-model="form.footer"
                type="text"
                class="mt-1 !mb-0"
                :class="{ '!border-n-ruby-8': !isFooterValid }"
                :maxlength="footerMaxLength"
                :aria-invalid="!isFooterValid"
              />
              <span
                class="flex items-center justify-between gap-2 text-xs"
                :class="isFooterValid ? 'text-n-slate-10' : 'text-n-ruby-11'"
              >
                <span>
                  {{
                    isFooterValid
                      ? $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FOOTER_LIMIT_HINT', {
                          max: footerMaxLength,
                        })
                      : $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FOOTER_LIMIT_ERROR', {
                          max: footerMaxLength,
                        })
                  }}
                </span>
                <span class="shrink-0 tabular-nums">
                  {{
                    $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CHARACTER_COUNT', {
                      current: footerLength,
                      max: footerMaxLength,
                    })
                  }}
                </span>
              </span>
            </label>
          </section>

          <section class="whatsapp-template-section">
            <div class="whatsapp-template-section-heading">
              <span class="whatsapp-template-section-icon">
                <i class="i-lucide-mouse-pointer-click size-4" />
              </span>
              <div>
                <h3>
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.SECTIONS.ACTIONS') }}
                </h3>
                <p>
                  {{
                    $t('INBOX_MGMT.WHATSAPP_TEMPLATES.SECTIONS.ACTIONS_HINT')
                  }}
                </p>
              </div>
            </div>
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.BUTTONS') }}
              </span>
              <NextButton
                ghost
                xs
                icon="i-lucide-plus"
                :disabled="form.buttons.length >= 3"
                :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.ADD_BUTTON')"
                @click="addButton"
              />
            </div>

            <div
              v-for="(button, index) in form.buttons"
              :key="index"
              class="p-3 rounded-lg bg-n-background mb-2 space-y-2 border border-n-weak"
            >
              <div
                class="grid grid-cols-[auto_minmax(0,1fr)_auto] gap-2 items-center"
              >
                <span
                  class="flex items-center justify-center w-6 h-6 text-xs font-medium rounded-md bg-n-alpha-2 text-n-slate-11"
                >
                  {{ index + 1 }}
                </span>
                <select v-model="button.type" class="!mb-0">
                  <option value="QUICK_REPLY">
                    {{
                      $t(
                        'INBOX_MGMT.WHATSAPP_TEMPLATES.BUTTON_TYPES.QUICK_REPLY'
                      )
                    }}
                  </option>
                  <option value="URL">
                    {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.BUTTON_TYPES.URL') }}
                  </option>
                  <option value="PHONE_NUMBER">
                    {{
                      $t(
                        'INBOX_MGMT.WHATSAPP_TEMPLATES.BUTTON_TYPES.PHONE_NUMBER'
                      )
                    }}
                  </option>
                </select>
                <NextButton
                  ghost
                  xs
                  ruby
                  icon="i-lucide-x"
                  :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.REMOVE')"
                  @click="removeButton(index)"
                />
              </div>
              <input
                v-model="button.text"
                type="text"
                class="!mb-0"
                :placeholder="
                  $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.BUTTON_TEXT')
                "
              />
              <input
                v-if="button.type === 'URL'"
                v-model="button.url"
                type="text"
                class="!mb-0"
                :placeholder="
                  $t('INBOX_MGMT.WHATSAPP_TEMPLATES.PLACEHOLDERS.URL')
                "
              />
              <input
                v-if="button.type === 'PHONE_NUMBER'"
                v-model="button.phone_number"
                type="text"
                class="!mb-0"
                :placeholder="
                  $t('INBOX_MGMT.WHATSAPP_TEMPLATES.PLACEHOLDERS.PHONE_NUMBER')
                "
              />
            </div>
          </section>
        </div>

        <aside class="whatsapp-template-preview">
          <h3 class="mb-4 text-sm font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW_TITLE') }}
          </h3>
          <div class="whatsapp-template-preview-phone">
            <div class="whatsapp-template-preview-row">
              <div class="whatsapp-template-preview-bubble">
                <div
                  v-if="previewHeader"
                  class="whatsapp-template-preview-header"
                >
                  {{ previewHeader }}
                </div>
                <div class="whatsapp-template-preview-text">
                  {{
                    previewBody ||
                    $t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW_EMPTY')
                  }}
                </div>
                <div
                  v-if="previewFooter"
                  class="whatsapp-template-preview-footer"
                >
                  {{ previewFooter }}
                </div>
              </div>
            </div>
            <div v-if="visibleButtons.length" class="mt-3 space-y-2">
              <div
                v-for="button in visibleButtons"
                :key="`${button.type}-${button.text}`"
                class="whatsapp-template-preview-button"
              >
                {{ button.text }}
              </div>
            </div>
          </div>
        </aside>
      </div>

      <div class="whatsapp-template-modal-footer">
        <NextButton
          ghost
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CANCEL')"
          @click="closeTemplateModal"
        />
        <NextButton
          :disabled="!canSave || isSaving"
          :is-loading="isSaving"
          :label="
            isEditing
              ? $t('INBOX_MGMT.WHATSAPP_TEMPLATES.UPDATE')
              : $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE')
          "
          @click="saveTemplate"
        />
      </div>
    </woot-modal>

    <woot-modal
      v-model:show="showDeleteConfirmModal"
      :on-close="closeDeleteConfirmModal"
    >
      <woot-modal-header
        :header-title="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE')"
        :header-content="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_CONFIRM')"
      />
      <div class="flex items-center justify-end gap-2 px-8 py-6">
        <NextButton
          ghost
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CANCEL')"
          @click="closeDeleteConfirmModal"
        />
        <NextButton
          ruby
          :is-loading="!!deletingTemplateId"
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE')"
          @click="deleteTemplate"
        />
      </div>
    </woot-modal>

    <woot-modal v-model:show="showFeedbackModal" :on-close="closeFeedbackModal">
      <woot-modal-header
        :header-title="feedbackTitle"
        :header-content="feedbackMessage"
      />
      <div class="flex items-center justify-end px-8 py-6">
        <NextButton
          :ruby="feedbackTone === 'error'"
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.UNDERSTOOD')"
          @click="closeFeedbackModal"
        />
      </div>
    </woot-modal>
  </div>
</template>

<style scoped>
:deep(.whatsapp-template-modal-shell) {
  background-color: rgb(var(--solid-1)) !important;
  border: 1px solid rgb(var(--slate-5)) !important;
  border-radius: 16px !important;
  box-shadow: 0 24px 80px rgb(0 0 0 / 42%) !important;
  max-height: calc(100vh - 72px) !important;
  overflow: hidden !important;
  width: min(1180px, calc(100vw - 48px)) !important;
}

.whatsapp-template-modal-header {
  align-items: center;
  background-color: rgb(var(--solid-2));
  border-bottom: 1px solid rgb(var(--slate-4));
  display: flex;
  gap: 12px;
  min-height: 56px;
  padding: 18px 24px;
}

.whatsapp-template-modal-icon,
.whatsapp-template-section-icon {
  align-items: center;
  background-color: rgb(var(--blue-4));
  border: 1px solid rgb(var(--blue-6));
  border-radius: 10px;
  color: rgb(var(--blue-11));
  display: flex;
  flex: 0 0 auto;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.whatsapp-template-modal-body {
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(0, 1.35fr) minmax(300px, 0.8fr);
  max-height: calc(100vh - 216px);
  min-width: 0;
  overflow: auto;
  padding: 20px 24px;
  width: 100%;
}

.whatsapp-template-form {
  display: grid;
  gap: 14px;
  min-width: 0;
  order: 1;
  width: 100%;
}

.whatsapp-template-section {
  background: linear-gradient(145deg, rgb(var(--slate-2)), rgb(var(--solid-1)));
  border: 1px solid rgb(var(--slate-4));
  border-radius: 12px;
  display: grid;
  gap: 14px;
  min-width: 0;
  padding: 18px;
  width: 100%;
}

.whatsapp-template-section-heading {
  align-items: flex-start;
  display: flex;
  gap: 10px;
  padding-bottom: 2px;
}

.whatsapp-template-section-heading h3 {
  color: rgb(var(--slate-12));
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25rem;
  margin: 0 0 2px;
}

.whatsapp-template-section-heading p {
  color: rgb(var(--slate-10));
  font-size: 0.75rem;
  line-height: 1.125rem;
  margin: 0;
}

.whatsapp-template-section-icon {
  border-radius: 8px;
  height: 30px;
  width: 30px;
}

.whatsapp-template-two-columns {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  min-width: 0;
  width: 100%;
}

.whatsapp-template-header-grid {
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
}

.whatsapp-template-field {
  display: grid;
  gap: 6px;
  min-width: 0;
  width: 100%;
}

.whatsapp-template-variable-type {
  max-width: 100% !important;
}

.whatsapp-template-section :deep(input),
.whatsapp-template-section :deep(select),
.whatsapp-template-section :deep(textarea) {
  background-color: rgb(var(--solid-1));
  border: 1px solid rgb(var(--slate-5));
  border-radius: 8px;
  box-sizing: border-box;
  color: rgb(var(--slate-12));
  display: block;
  max-width: none !important;
  min-height: 40px;
  min-width: 0 !important;
  width: 100% !important;
}

.whatsapp-template-section :deep(input:focus),
.whatsapp-template-section :deep(select:focus),
.whatsapp-template-section :deep(textarea:focus) {
  border-color: rgb(var(--teal-8));
  box-shadow: 0 0 0 3px rgb(var(--teal-5) / 24%);
}

.whatsapp-template-section :deep(textarea) {
  min-height: 128px;
  resize: vertical;
}

.whatsapp-template-preview {
  align-self: start;
  min-width: 0;
  order: 2;
  position: sticky;
  top: 0;
  width: 100%;
}

.whatsapp-template-preview > h3 {
  align-items: center;
  display: flex;
  gap: 8px;
}

.whatsapp-template-preview > h3::before {
  background-color: rgb(var(--teal-9));
  border-radius: 999px;
  content: '';
  height: 8px;
  width: 8px;
}

.whatsapp-template-preview-phone {
  background: radial-gradient(
      circle at 16px 16px,
      rgb(17 94 89 / 10%) 0 1px,
      transparent 1px
    ),
    linear-gradient(180deg, #e7ddd0, #d8ccbd);
  background-size:
    18px 18px,
    auto;
  border: 1px solid rgb(var(--slate-5));
  border-radius: 12px;
  min-height: 320px;
  overflow: hidden;
  padding: 22px;
}

:global(.dark) .whatsapp-template-preview-phone {
  background: radial-gradient(
      circle at 16px 16px,
      rgb(255 255 255 / 5%) 0 1px,
      transparent 1px
    ),
    linear-gradient(180deg, #17211f, #111817);
}

.whatsapp-template-preview-row {
  display: flex;
  justify-content: flex-end;
}

.whatsapp-template-preview-bubble {
  background-color: #dcf8c6;
  border-radius: 10px 10px 2px 10px;
  box-shadow: 0 1px 2px rgb(17 24 39 / 22%);
  color: #111b21;
  max-width: min(100%, 25rem);
  padding: 12px 14px;
}

.whatsapp-template-preview-header {
  color: #111b21;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.25rem;
  margin-bottom: 0.5rem;
  overflow-wrap: anywhere;
}

.whatsapp-template-preview-text {
  color: #111b21;
  font-size: 0.875rem;
  line-height: 1.35rem;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.whatsapp-template-preview-footer {
  border-top: 1px solid rgb(17 24 39 / 10%);
  color: #667781;
  font-size: 0.75rem;
  margin-top: 0.75rem;
  overflow-wrap: anywhere;
  padding-top: 0.5rem;
}

:global(.dark) .whatsapp-template-preview-bubble {
  background-color: #005c4b;
  color: #f4fbf8;
}

:global(.dark) .whatsapp-template-preview-header,
:global(.dark) .whatsapp-template-preview-text {
  color: #f4fbf8;
}

:global(.dark) .whatsapp-template-preview-footer {
  border-top-color: rgb(255 255 255 / 12%);
  color: #bfd8d3;
}

.whatsapp-template-preview-button {
  background-color: rgb(255 255 255 / 86%);
  border: 1px solid rgb(17 24 39 / 8%);
  border-radius: 10px;
  color: #0b7a75;
  font-weight: 600;
  padding: 9px 12px;
  text-align: center;
}

:global(.dark) .whatsapp-template-preview-button {
  background-color: #1f2c2b;
  border-color: rgb(255 255 255 / 7%);
  color: #68d4ca;
}

.whatsapp-template-modal-footer {
  align-items: center;
  background-color: rgb(var(--solid-1));
  border-top: 1px solid rgb(var(--slate-4));
  display: flex;
  gap: 10px;
  min-height: 72px;
  justify-content: flex-end;
  padding: 16px 24px;
}

@media (max-width: 960px) {
  :deep(.whatsapp-template-modal-shell) {
    max-height: calc(100vh - 24px) !important;
    width: calc(100vw - 24px) !important;
  }

  .whatsapp-template-modal-body {
    grid-template-columns: 1fr;
    max-height: calc(100vh - 168px);
    padding: 16px;
  }

  .whatsapp-template-preview {
    order: 1;
    position: static;
  }

  .whatsapp-template-preview-phone {
    min-height: 240px;
  }

  .whatsapp-template-form {
    order: 2;
  }

  .whatsapp-template-two-columns,
  .whatsapp-template-header-grid {
    grid-template-columns: 1fr;
  }

  .whatsapp-template-modal-header,
  .whatsapp-template-modal-footer {
    padding-left: 16px;
    padding-right: 16px;
  }
}
</style>
